// src/api/dnd.js
const BASE = 'https://www.dnd5eapi.co/api';

export async function getWeapons() {
  const res = await fetch(`${BASE}/equipment-categories/weapon`);
  const data = await res.json();
  return data.equipment || [];
}
export async function getWeaponDetail(index) {
  const res = await fetch(`${BASE}/equipment/${index}`);
  const data = await res.json();
  return {
    name: data.name,
    damage: data.damage?.damage_dice || '1d6',
    desc: data.desc?.[0] || '',
  };
}

/* ----- dice -> expected average (อ้างอิงจาก API แต่แปลงเป็นตัวเลข) ----- */
export function avgFromDice(diceStr) {
  if (!diceStr) return null;
  const m = diceStr.match(/(\d+)d(\d+)([+\-]\d+)?/i);
  if (!m) return null;
  const n = +m[1], d = +m[2], k = m[3] ? +m[3] : 0;
  return Math.round(n * (d + 1) / 2 + k);
}

/* ----- Beginner Weapons (คัดจาก API + คำนวณ Base DMG) ----- */
export async function getBeginnerWeapons(limit = 8) {
  const list = await getWeapons();
  const simple = list.filter(w =>
    /club|dagger|quarterstaff|shortsword|handaxe|sickle|spear|light crossbow|sling/i.test(w.name)
  ).slice(0, limit);

  const detailed = await Promise.all(simple.map(async (w) => {
    const det = await getWeaponDetail(w.index);
    const base = avgFromDice(det.damage) ?? 4; // ถ้าอ่านไม่ได้ให้ base 4
    return { index: w.index, name: det.name, base, sourceDice: det.damage, desc: det.desc };
  }));
  return detailed;
}

/* ----- Beginner Spells (3 อัน/คลาส) จาก API + fallback ----- */
const CLASS_MAP = {
  Warrior: ['paladin','ranger','cleric'], // fighter ไม่มีสกิล → map ไปที่มี
  Mage:    ['wizard','sorcerer'],
  Rogue:   ['ranger','bard'],             // rogue ไม่มีสกิลใน API → map
};

async function fetchClassSpells(dndClass) {
  const r = await fetch(`${BASE}/classes/${dndClass}/spells`);
  if (!r.ok) return [];
  const j = await r.json();
  return j.results || [];
}
async function fetchSpellDetail(idx) {
  const r = await fetch(`${BASE}/spells/${idx}`);
  if (!r.ok) return null;
  return await r.json();
}

/** คืนสกิลแบบอ่านง่าย:
 *  { key, name, desc, kind: 'damage'|'heal'|'buff', base: number, explain, sourceDice? }
 */
export async function getBeginnerSpellsForClass(cls, want = 3) {
  const chain = CLASS_MAP[cls] || ['wizard'];
  let pool = [];
  for (const c of chain) {
    const list = await fetchClassSpells(c);
    pool = pool.concat(list);
  }
  const picks = pool.slice(0, 20);
  const details = (await Promise.all(picks.map(x => fetchSpellDetail(x.index))))
    .filter(Boolean)
    .filter(d => (d.level ?? 0) <= 1);

  const toSkill = (d) => {
    const dice = d.damage?.damage_dice || d.damage?.damage_at_slot_level?.["1"] || null;
    let base = avgFromDice(dice);
   // 🩸 fallback สำหรับสกิลที่ไม่มี damage field
    if (!base) {
      const text = JSON.stringify(d).toLowerCase();
      if (text.includes('damage') && !text.includes('heal')) base = 6;  // assume weak dmg
      else if (text.includes('heal')) base = 6;
      else base = 0;
    }

    let kind = 'buff', explain = 'เพิ่มความสามารถชั่วคราว';
    const blob = JSON.stringify(d).toLowerCase();
    if (d.heal_at_slot_level || blob.includes('heal') || blob.includes('hit points')) {
      kind = 'heal'; explain = 'ฟื้นฟูพลังชีวิต';
    } else if (d.attack_type || d.damage || /(damage|bolt|missile|flame|ray|acid)/i.test(blob)) {
      kind = 'damage'; explain = 'สร้างความเสียหายเดี่ยว';
    }
    return {
      key: d.index,
      name: d.name,
      desc: d.desc?.[0] || '',
      kind,
      base: Math.max(0, base),
      explain,
      sourceDice: dice || null,
    };
  };

  const skills = details.map(toSkill);

  const fallback = [
    { key:'arcane-bolt',  name:'Arcane Bolt',  kind:'damage', base:7, desc:'ยิงพลังงานเวท', explain:'สร้างความเสียหายเดี่ยว', sourceDice:null },
    { key:'heroic-aid',   name:'Heroic Aid',   kind:'heal',   base:6, desc:'ฮีลเล็กน้อย',   explain:'ฟื้นฟูพลังชีวิต',       sourceDice:null },
    { key:'battle-focus', name:'Battle Focus', kind:'buff',   base:0, desc:'โฟกัส 1 เทิร์น', explain:'เพิ่มความสามารถชั่วคราว', sourceDice:null },
  ];

  const out = [...skills.slice(0, want)];
  while (out.length < want) out.push(fallback[out.length % fallback.length]);
  return out.slice(0, want);
}
